import MediaFormat from './MediaFormat'
import { Row, Form } from 'react-bootstrap'
import { Button } from '@/components/ui/button'
import { CubeLoader } from '@/components/ui/cube-loader'
import { useQuery, useMutation } from '@apollo/client'
import WizardSteps from '@/components/common/wizard/WizardSteps'
import { useEffect, useReducer, useState, useCallback, useRef } from 'react'
import { useWizard } from '@/components/mediaBuying/adCreatives/AdCreativeWizard'
import { mediaCropReducer, initialState, ACTIONS } from '@/reducers/mediaCropReducer'
import {
    UPDATE_AD_ASSET_CROP_DATA,
    GET_AD_CREATIVE_ASSET_VARATIONS_BY_AD_CREATIVE_ID,
} from '@/graphql/ads/ad-assets-queries'

const Navigation = ({ goBack, goNext }) => {
    return (
        <div className="d-flex gap-2">
            <Button variant="secondary" onClick={goBack} className="tw:w-[156px] tw:!text-sm">
                Back
            </Button>
            <Button onClick={goNext} className="tw:w-[156px] tw:!text-sm">
                Next
            </Button>
        </div>
    )
}

const SetMediaCrop = () => {
    const {
        id: adCreativeAssetId,
        setStepContentData,
        currentStep,
        markStepAs,
        goToStep,
        campaignFormat,
        registerCustomNavigation,
        clearCustomNavigation,
        getStepContentData,
        removeStepContentData,
        dataForStepper,
    } = useWizard()
    const { assetId } = getStepContentData()

    // A dimension replacement started from here and abandoned must not narrow the next upload
    useEffect(() => {
        removeStepContentData('uploadAssets', 'replaceFormat')
    }, [])

    const [state, dispatch] = useReducer(mediaCropReducer, initialState)
    const [variationsLoading, setVariationsLoading] = useState(true)
    const [cropRatios] = useState(['square', 'vertical', 'horizontal'])
    const selectFormatData = getStepContentData('selectFormat') || {}
    const adFormat = selectFormatData.format
    const hasLeadForm = selectFormatData.showLeadForm || false

    useQuery(GET_AD_CREATIVE_ASSET_VARATIONS_BY_AD_CREATIVE_ID, {
        variables: { adCreativeId: adCreativeAssetId },
        fetchPolicy: 'network-only',

        onCompleted: (data) => {
            const varationsObject = data.AdCreativeAssetVariation.getByAdCreativeId

            let variations = Object.values(varationsObject)

            if (adFormat === 'carousel') {
                variations = variations.filter((x) => x.ad_creative_asset_id === assetId)
            }

            variations = variations.map((variant) => {
                const start = variant.adCreativeAsset.trim_data?.start
                const end = variant.adCreativeAsset.trim_data?.end

                const trimData = {
                    start: start ? start / 1000 : null,
                    end: end ? end / 1000 : null,
                }

                return { ...variant, trim_data: trimData, duration: variant.adCreativeAsset.duration }
            })

            initializeCrops(variations)
            setVariationsLoading(false)
        },
    })

    const [updateAdAssetCropData] = useMutation(UPDATE_AD_ASSET_CROP_DATA)
    const [croppedArea, setCroppedArea] = useState({})

    const initializeCrops = async (variations) => {
        // Load existing crops from database
        cropRatios.forEach((ratio) => {
            const variant = variations.find((variation) => variation.format === ratio)

            if (variant) {
                const payload = {
                    cropId: ratio,
                    croppedData: variant.crop_data,
                    selectedOption: variant.cropped ? 'cropped' : 'original',
                    originalUrl: variant.adCreativeAsset.url,
                    adCreativeAsset: variant.adCreativeAsset,
                    trimData: variant.trim_data,
                    duration: variant.duration,
                }
                dispatch({
                    type: ACTIONS.UPDATE,
                    payload,
                })
            }
        })
    }

    const saveCropData = (assetId, data) => updateAdAssetCropData({ variables: { assetId, cropData: data } })

    const onCrop = async (ratio, assetId, aspectCropId, croppedArea, croppedAreaPixels, zoom) => {
        setCroppedArea({ ...croppedArea, zoom })
        const croppedData = { ...croppedAreaPixels, zoom }

        dispatch({
            type: ACTIONS.UPDATE_CROP,
            payload: { aspectCropId, croppedData },
        })

        const data = {
            name: aspectCropId,
            x: croppedAreaPixels.x,
            y: croppedAreaPixels.y,
            width: croppedAreaPixels.width,
            height: croppedAreaPixels.height,
            cropped: true,
            zoom,
            ratio,
        }

        saveCropData(assetId, data)
    }

    const onOptionChange = async (assetId, aspectCropId, selectedOption) => {
        dispatch({
            type: ACTIONS.UPDATE_OPTION,
            payload: { cropId: aspectCropId, selectedOption },
        })

        const crop = state.aspectCropData.find((crop) => crop.id === aspectCropId)

        const data = {
            name: crop.id,
            ratio: crop.ratio,
            x: crop.croppedData.x,
            y: crop.croppedData.y,
            width: crop.croppedData.width,
            height: crop.croppedData.height,
            zoom: crop.croppedData.zoom,
            cropped: selectedOption === 'cropped',
        }

        saveCropData(assetId, data)
    }

    // Function to check if different formats have different assets
    const checkIfAssetsAreCustomized = useCallback(() => {
        if (adFormat === 'carousel') {
            // For carousel, check if different cards are used for different formats
            // This would need more complex logic based on your carousel implementation
            return false
        }

        // Get all unique asset IDs from the aspect crop data that have been populated
        const assetIds = new Set()
        const assetsWithData = state.aspectCropData.filter((crop) => crop.adCreativeAsset && crop.adCreativeAsset.id)

        if (assetsWithData.length === 0) {
            // No asset data loaded yet, can't determine customization
            return false
        }

        assetsWithData.forEach((crop) => {
            assetIds.add(crop.adCreativeAsset.id)
        })

        // If there's more than one unique asset ID, it means different formats use different assets
        return assetIds.size > 1
    }, [adFormat, state.aspectCropData])

    // Use refs to store the latest function references
    const goBackRef = useRef()
    const goNextRef = useRef()

    // Create stable function references
    const goBack = useCallback(() => {
        goToStep(calculatePreviousStep())
    }, [campaignFormat])

    const calculatePreviousStep = (isReplacing = false) => {
        const stepData = getStepContentData('uploadAssets')
        const selectedOption = stepData?.selectedOption

        if (campaignFormat === 'video' && !isReplacing) return 'setVideoThumbnail'
        if (campaignFormat === 'video' && isReplacing) return 'uploadAssets'
        if (campaignFormat === 'carousel') return 'carouselCards'

        if (stepData || selectedOption) {
            if (selectedOption === 'textToImage') return 'aiTextToImage'
            if (selectedOption === 'imageToImage') return 'aiImageToImage'
            if (selectedOption === 'aiAssetLibrary') return 'aiAssetLibrary'
        }

        return 'uploadAssets'
    }

    const goNext = useCallback(() => {
        // Check if assets are customized (different assets for different formats)
        const hasCustomizedAssets = checkIfAssetsAreCustomized()

        // Save crop state so previews on next steps show the same images as this step
        const cropState = state.aspectCropData.map((c) => ({
            id: c.id,
            croppedData: c.croppedData,
            selectedOption: c.selectedOption,
            originalUrl: c.originalUrl,
        }))
        setStepContentData({ hasCustomizedAssets, cropState }, 'setMediaCrop')

        if (campaignFormat === 'carousel') {
            goToStep('carouselCards')
        } else {
            markStepAs(1, currentStep)
            goToStep('setAdContent')
        }
    }, [campaignFormat, currentStep, checkIfAssetsAreCustomized, state.aspectCropData])

    // Update refs with latest functions
    goBackRef.current = goBack
    goNextRef.current = goNext

    const handleReplaceClick = (format) => {
        if (campaignFormat === 'video' || campaignFormat === 'singleImage') {
            setStepContentData({ replaceFormat: format }, 'uploadAssets')
        }

        if (campaignFormat === 'carousel') {
            const cropStepData = getStepContentData() || {}
            setStepContentData(
                {
                    replaceAssetId: cropStepData.assetId,
                    replaceAssetPosition: cropStepData.replaceAssetPosition,
                    existingCardIds: cropStepData.existingCardIds,
                },
                'uploadAssets',
            )
            goToStep('uploadAssets')
        } else {
            goToStep(calculatePreviousStep(true))
        }
    }

    // Custom navigation for media crop screen - only run once
    useEffect(() => {
        const navigationComponent = (
            <Navigation goBack={() => goBackRef.current?.()} goNext={() => goNextRef.current?.()} />
        )

        registerCustomNavigation(navigationComponent)

        return () => clearCustomNavigation()
    }, []) // Empty dependency array - only run once

    if (variationsLoading)
        return (
            <div className="tw:w-full tw:flex tw:items-center tw:justify-center">
                <CubeLoader size="default" fullScreen={true} />
            </div>
        )

    const calculateTrimParams = (trimData) => {
        let start = null
        let end = null

        // The trim data is already in seconds from initializeCrops, just pass it through
        if (trimData?.start !== null && trimData?.start !== undefined) start = trimData.start
        if (trimData?.end !== null && trimData?.end !== undefined) end = trimData.end

        return { start, end }
    }

    return (
        <div className="tw:flex tw:h-full">
            <WizardSteps adCreative={dataForStepper} currentStep="setMediaCrop" />
            <div className="flex-grow-1 p-4 overflow-auto">
                <div className="media-crop-container" style={{ fontSize: 14 }}>
                    <p>
                        We evaluated your image and cropped it to fit the placement when we think it may perform better.
                        You can edit these crops for each placement.
                    </p>
                    <Form.Group className="mb-3">
                        <Form.Check
                            type="checkbox"
                            checked={state.flexibleMedia}
                            onChange={(e) => dispatch({ type: ACTIONS.SET_FLEXIBLE_MEDIA, payload: e.target.checked })}
                            label={
                                <div style={{ marginLeft: 8 }}>
                                    <strong>
                                        Turn on flexible media to show media that you chose for a specific aspect ratio
                                        across all placements.
                                    </strong>
                                    <div className="form-check-description">
                                        Media may be cropped to fit certain placements and will be shown when likely to
                                        improve performance. You can turn this off at any time.
                                    </div>
                                </div>
                            }
                        />
                    </Form.Group>
                    <Row className="g-4 tw:w-fit">
                        {state.aspectCropData.map((crop) => (
                            <MediaFormat
                                area={croppedArea}
                                {...crop}
                                key={crop.id}
                                type={crop.adCreativeAsset?.type}
                                croppedAreaPixels={crop.croppedData}
                                canvasWidth={crop.canvasWidth}
                                canvasHeight={crop.canvasHeight}
                                originalUrl={crop.originalUrl}
                                handleReplaceClick={() => handleReplaceClick(crop.id)}
                                onOptionChange={(option) => onOptionChange(crop.adCreativeAsset.id, crop.id, option)}
                                startTime={calculateTrimParams(crop.trimData).start}
                                endTime={calculateTrimParams(crop.trimData).end}
                                videoDuration={crop.duration}
                                thumbnails={crop.adCreativeAsset?.thumbnails}
                                onCrop={({ croppedArea, croppedAreaPixels, zoom }) =>
                                    onCrop(
                                        crop.ratio,
                                        crop.adCreativeAsset.id,
                                        crop.id,
                                        croppedArea,
                                        croppedAreaPixels,
                                        zoom,
                                    )
                                }
                            />
                        ))}
                    </Row>
                </div>
            </div>
        </div>
    )
}

export default SetMediaCrop
